'use client';
import Image from 'next/image';
import * as React from 'react';
import { useTranslations } from 'next-intl';

import { Store } from '@/lib/types/customer/restaurant/StoreProfile';

import { Info, VerifiedIcon } from '@/components/ui/Icons';
import SvgStar from '@/components/ui/Icons/Star';
import useAppLocale from '@/hooks/useAppLocale';
import ImpressumModal from './ImpressumModal';
import STRINGS, { STOREFRONT } from '@/lib/utils/translation';
import { HeartIcon } from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid } from '@heroicons/react/24/solid';
import { cn } from '@/lib/utils';
import { useFavoriteHandler } from '@/hooks/useFavoriteHandler';
import FavoriteLoginModal from '../FavoriteLoginModal';

interface RestaurantMonogramProps {
  store: Store;
}

const RestaurantMonogram = ({ store }: RestaurantMonogramProps) => {
  const t = useTranslations();
  const [openInfoModal, setOpenInfoModal] = React.useState<boolean>(false);
  const { lang } = useAppLocale();
  const handleImpressumClick = (event: React.MouseEvent<HTMLDivElement>) => {
    event.stopPropagation();
    setOpenInfoModal(true);
  };

  const hasImpressum = !!store?.meta?.[0].impressum?.[lang];

  const {
    isFavorite,
    toggleFavorite,
    isFavoriteToggleLoading,
    showLoginModal,
    setShowLoginModal,
    redirectToLogin,
  } = useFavoriteHandler('restaurant', store.id);

  const latestDiscountCode = store?.discount_codes || [];

  return (
    <div className="container relative z-10 -mt-16">
      <div className="space-y-4 rounded-md bg-white px-6 py-10 shadow-lg sm:w-[360px]">
        <div className="flex items-start justify-start space-x-5">
          <div className="relative h-[60px] w-[60px] shrink-0 overflow-hidden rounded">
            <Image
              src={
                store.media?.logo?.[0]?.thumb_url ?? '/res-logo-placeholder.png'
                // '/store-icon-placeholder.jpeg'
              }
              alt={store.name + 'logo'}
              fill
              className="shrink-0 object-cover"
            />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="store-name flex items-center justify-start space-x-2">
                <h1
                  className="max-w-[160px] truncate font-bold text-title"
                  title={store.name}
                >
                  {store.name}
                </h1>
                {store.features.ui_storefront_show_verified_mark_in_profile && (
                  <div className="verification-icon">
                    <VerifiedIcon color="green" />
                  </div>
                )}
                {hasImpressum && (
                  <div
                    role="button"
                    tabIndex={0} // Add tabIndex to make the element focusable
                    className="flex flex-grow justify-end text-title transition-colors focus-within:border-none focus-within:outline-none hover:text-primary-700 focus:border-none focus:outline-none focus-visible:border-none focus-visible:outline-none"
                    onClick={(e) => handleImpressumClick(e)}
                  >
                    <Info />
                  </div>
                )}
              </div>
              <button
                onClick={toggleFavorite}
                disabled={isFavoriteToggleLoading}
                className={cn(
                  store?.aff_commission_formatted &&
                    store.discount_codes &&
                    store.discount_codes?.length > 0
                    ? 'mr-2'
                    : ''
                )}
                aria-label={
                  isFavorite
                    ? t(`${STOREFRONT.FAVORITES}.remove_from_favorite`)
                    : t(`${STOREFRONT.FAVORITES}.add_to_favorite`)
                }
              >
                {isFavorite ? (
                  <HeartIconSolid className="h-6 w-6 text-primary" />
                ) : (
                  <HeartIcon className="h-6 w-6 text-gray-400" />
                )}
              </button>
            </div>
            {store.short_description && (
              <div className="">
                <span className="text-sm text-paragraph">
                  {store.short_description}
                </span>
              </div>
            )}
          </div>
        </div>
        {/* Review and availability */}
        <div className="reviews-availability flex items-center justify-start space-x-3">
          {store.avg_total_rating.counts.all > 0 && (
            <div className="review-rating flex items-center justify-start space-x-2">
              <div>
                <SvgStar color="#FFC107" />
              </div>
              <div className=" text-lg font-bold text-paragraph">
                {store.avg_total_rating?.formatted.all}
              </div>
              <div className="text-sm text-title">
                ({store.total_reviews} {t(STRINGS.COMMON + '.reviews')})
              </div>
            </div>
          )}
          <div className="availability text-xs">
            {store.allows_delivery && (
              <span className="hidden rounded-full border border-green-200 px-2 py-1 text-green-600">
                {t(STRINGS.SEARCH + '.delivery_available')}
              </span>
            )}
          </div>
        </div>
        <div className="flex flex-col justify-start space-y-3 sm:flex-row sm:items-end sm:space-x-3 sm:space-y-0">
          {latestDiscountCode && latestDiscountCode?.length > 0 && (
            <div className="inline-flex items-center justify-center space-x-1 rounded-3xl border-2 border-pink-200 bg-pink-50 px-4 py-2 uppercase text-pink-500">
              <span className="text-xs font-medium leading-[14px]">
                {/* {latestDiscountCode?.[0]?.cashback} */}
                {latestDiscountCode?.[0]?.cashback} {t('common.discount_code')}
              </span>
              {/* <CheckIcon className="h-3.5 stroke-[3]" /> */}
            </div>
          )}
          {store?.aff_commission_formatted && (
            <div className="inline-flex items-center justify-center rounded-3xl border-2 border-green-200 bg-green-50 px-4 py-2 uppercase">
              <span className="text-xs font-medium leading-[14px] text-green-500">
                {store?.aff_commission_formatted}
              </span>
            </div>
          )}
        </div>
      </div>
      <ImpressumModal
        openInfoModal={openInfoModal}
        setOpenInfoModal={setOpenInfoModal}
        content={store.meta?.[0].impressum?.[lang]}
      />
      <FavoriteLoginModal
        isOpen={showLoginModal}
        setIsOpen={setShowLoginModal}
        redirectToLogin={redirectToLogin}
      />
    </div>
  );
};

export default RestaurantMonogram;
